/**
 * 表格行列控制浮层（对齐 RichTextEditor floating/table）：
 * - 单元格子选态：行/列握把 + 表内行列 resize 线
 * - 悬停（非仅表级选中）：下方加行条、右侧加列条
 */
import type { Key, MouseEvent, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown,Separator } from '@heroui/react';
import {
  IconArrowAutofitDown,
  IconArrowAutofitLeft,
  IconArrowAutofitRight,
  IconArrowAutofitUp,
  IconColumnInsertLeft,
  IconColumnInsertRight,
  IconCopy,
  IconGripHorizontal,
  IconGripVertical,
  IconPlus,
  IconRowInsertBottom,
  IconRowInsertTop,
  IconTrash,
} from '@tabler/icons-react';
import type { TableElement } from '@/components/canvas/document/publicTypes';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { SelectionChromeFrame } from '@/components/canvas/selection/SelectionChromeFrame';
import {
  COL_RESIZE_HIT_PX,
  ROW_RESIZE_HIT_PX,
  TABLE_ADD_COL_STRIP_WIDTH_PX,
  TABLE_ADD_ROW_STRIP_HEIGHT_PX,
  TABLE_ADD_STRIP_GAP_PX,
  TABLE_ADD_STRIP_ICON_PX,
  TABLE_COL_GRIP_HEIGHT_PX,
  TABLE_COL_GRIP_WIDTH_PX,
  TABLE_DRAG_THRESHOLD_PX,
  TABLE_ROW_GRIP_HEIGHT_PX,
  TABLE_ROW_GRIP_WIDTH_PX,
} from '@/components/canvas/elements/table/constants';
import { tableLayoutSize } from '@/components/canvas/elements/table/utils/tableLayout';
import {
  patchDeleteColumn,
  patchDeleteRow,
  patchDuplicateColumn,
  patchDuplicateRow,
  patchInsertColumn,
  patchInsertColumnAtEnd,
  patchInsertRow,
  patchInsertRowAtEnd,
  patchMoveColumn,
  patchMoveColumnTo,
  patchMoveRow,
  patchMoveRowTo,
} from '@/components/canvas/elements/table/utils/tableStructure';
import {
  colMoveToIndex,
  colScreenBox,
  resolveCanvasColLineDrag,
  resolveCanvasRowLineDrag,
  rowMoveToIndex,
  rowScreenBox,
} from '@/components/canvas/elements/table/utils/tableLineDragCanvas';
import {
  buildColDragGhostTable,
  buildRowDragGhostTable,
  getCanvasTableDom,
  getTableScreenScale,
  hideCanvasTableColumnContents,
  hideCanvasTableRowContents,
  resetBodyDragChromeStyles,
  restoreSourceContentHidden,
  type SourceContentHidden,
} from '@/components/canvas/elements/table/utils/tableLineDragGhost';

type LineDragVisual =
  | {
      kind: 'row';
      tableRect: DOMRect;
      rowIndex: number;
      rowBox: ReturnType<typeof rowScreenBox>;
      offsetY: number;
      dropLine: { top: number; left: number; width: number; height: number } | null;
    }
  | {
      kind: 'col';
      tableRect: DOMRect;
      colIndex: number;
      colBox: ReturnType<typeof colScreenBox>;
      offsetX: number;
      dropLine: { left: number; top: number; width: number; height: number } | null;
    };

export interface TableAxisControlsOverlayProps {
  element: TableElement;
  rows: number;
  cols: number;
  rowHeights: number[];
  colWidths: number[];
  selectedCol: number | null;
  selectedRow: number | null;
  anchorRow: number;
  anchorCol: number;
  containerRef: RefObject<HTMLDivElement | null>;
  /** 显示行列握把、边线 resize（单元格编辑中隐藏） */
  showAxisControls: boolean;
  /** 显示末尾加行/加列条（单元格选中或编辑中显示） */
  showAddStrips: boolean;
  onRowEdgeResize: (edge: 'bottom', e: MouseEvent) => void;
  onColEdgeResize: (edge: 'right', e: MouseEvent) => void;
  onRowResize: (rowIndex: number, e: MouseEvent) => void;
  onColResize: (colIndex: number, e: MouseEvent) => void;
  /** 行列线拖拽进行中时通知父级，用于隐藏原位置选框 */
  onLineDragChange?: (kind: 'row' | 'col' | null) => void;
}

function sumBefore(values: number[], index: number): number {
  return values.slice(0, index).reduce((a, b) => a + b, 0);
}

export function TableAxisControlsOverlay({
  element,
  rows,
  cols,
  rowHeights,
  colWidths,
  selectedCol,
  selectedRow,
  anchorRow,
  anchorCol,
  containerRef,
  showAxisControls,
  showAddStrips,
  onRowEdgeResize,
  onColEdgeResize,
  onRowResize,
  onColResize,
  onLineDragChange,
}: TableAxisControlsOverlayProps) {
  const { updateElement, setSelectedTableColumn, setSelectedTableRow, setSelectedTableCells, selectElement, zoom } =
    useCanvas();

  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  const [rowMenuOpen, setRowMenuOpen] = useState(false);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [lineDragVisual, setLineDragVisual] = useState<LineDragVisual | null>(null);
  const [resizingColIndex, setResizingColIndex] = useState<number | null>(null);
  const [resizingRowIndex, setResizingRowIndex] = useState<number | null>(null);
  const rowDragRef = useRef<{
    pointerId: number;
    startY: number;
    dragging: boolean;
    fromIndex: number;
    tableRect: DOMRect;
    rowBox: ReturnType<typeof rowScreenBox>;
    sourceContentHidden?: SourceContentHidden[];
  } | null>(null);
  const colDragRef = useRef<{
    pointerId: number;
    startX: number;
    dragging: boolean;
    fromIndex: number;
    tableRect: DOMRect;
    colBox: ReturnType<typeof colScreenBox>;
    sourceContentHidden?: SourceContentHidden[];
  } | null>(null);

  const rowLineDragGhostMountRef = useRef<HTMLDivElement | null>(null);
  const colLineDragGhostMountRef = useRef<HTMLDivElement | null>(null);
  const rowLineDragGhostTableRef = useRef<HTMLTableElement | null>(null);
  const colLineDragGhostTableRef = useRef<HTMLTableElement | null>(null);
  const suppressRowMenuClickRef = useRef(false);
  const suppressColMenuClickRef = useRef(false);

  useEffect(() => {
    setPortalEl(document.body);
  }, []);

  useEffect(() => {
    const kind = lineDragVisual?.kind === 'row' ? 'row' : lineDragVisual?.kind === 'col' ? 'col' : null;
    onLineDragChange?.(kind);
    if (lineDragVisual) {
      setRowMenuOpen(false);
      setColMenuOpen(false);
    }
  }, [lineDragVisual, onLineDragChange]);

  useLayoutEffect(() => {
    if (lineDragVisual?.kind === 'row') {
      const m = rowLineDragGhostMountRef.current;
      const t = rowLineDragGhostTableRef.current;
      if (m && t && m.firstChild !== t) m.replaceChildren(t);
    } else {
      rowLineDragGhostMountRef.current?.replaceChildren();
      if (lineDragVisual?.kind !== 'col') rowLineDragGhostTableRef.current = null;
    }

    if (lineDragVisual?.kind === 'col') {
      const m = colLineDragGhostMountRef.current;
      const t = colLineDragGhostTableRef.current;
      if (m && t && m.firstChild !== t) m.replaceChildren(t);
    } else {
      colLineDragGhostMountRef.current?.replaceChildren();
      if (lineDragVisual?.kind !== 'row') colLineDragGhostTableRef.current = null;
    }
  }, [lineDragVisual]);

  const endRowLineDragVisual = useCallback(() => {
    restoreSourceContentHidden(rowDragRef.current?.sourceContentHidden);
    resetBodyDragChromeStyles();
    rowLineDragGhostMountRef.current?.replaceChildren();
    rowLineDragGhostTableRef.current = null;
    setLineDragVisual((v) => (v?.kind === 'row' ? null : v));
  }, []);

  const endColLineDragVisual = useCallback(() => {
    restoreSourceContentHidden(colDragRef.current?.sourceContentHidden);
    resetBodyDragChromeStyles();
    colLineDragGhostMountRef.current?.replaceChildren();
    colLineDragGhostTableRef.current = null;
    setLineDragVisual((v) => (v?.kind === 'col' ? null : v));
  }, []);

  const { width: layoutWidth, height: layoutHeight } = tableLayoutSize(colWidths, rowHeights);
  const activeRow = selectedRow ?? anchorRow;
  const activeCol = selectedCol ?? anchorCol;
  const menuRow = selectedRow ?? activeRow;
  const menuCol = selectedCol ?? activeCol;

  const applyPatch = useCallback(
    (patch: ReturnType<typeof patchInsertColumn> | null) => {
      if (!patch) return;
      updateElement(element.id, patch);
    },
    [element.id, updateElement],
  );

  const selectColumn = useCallback(
    (col: number) => {
      selectElement(element.id, false);
      setSelectedTableColumn({ elementId: element.id, col });
      setSelectedTableRow(null);
      setSelectedTableCells(null);
    },
    [element.id, selectElement, setSelectedTableColumn, setSelectedTableRow, setSelectedTableCells],
  );

  const selectRow = useCallback(
    (row: number) => {
      selectElement(element.id, false);
      setSelectedTableRow({ elementId: element.id, row });
      setSelectedTableColumn(null);
      setSelectedTableCells(null);
    },
    [element.id, selectElement, setSelectedTableColumn, setSelectedTableRow, setSelectedTableCells],
  );

  const onColumnMenuAction = useCallback(
    (col: number, key: Key) => {
      if (typeof key === 'string' && key.startsWith('sep-')) return;
      switch (key) {
        case 'insert-left':
          applyPatch(patchInsertColumn(element, col, 'left'));
          setSelectedTableColumn({ elementId: element.id, col });
          break;
        case 'insert-right':
          applyPatch(patchInsertColumn(element, col, 'right'));
          setSelectedTableColumn({ elementId: element.id, col: col + 1 });
          break;
        case 'duplicate':
          applyPatch(patchDuplicateColumn(element, col));
          setSelectedTableColumn({ elementId: element.id, col: col + 1 });
          break;
        case 'move-left':
          applyPatch(patchMoveColumn(element, col, 'left'));
          if (col > 0) setSelectedTableColumn({ elementId: element.id, col: col - 1 });
          break;
        case 'move-right':
          applyPatch(patchMoveColumn(element, col, 'right'));
          if (col < cols - 1) setSelectedTableColumn({ elementId: element.id, col: col + 1 });
          break;
        case 'delete': {
          const p = patchDeleteColumn(element, col);
          if (p) {
            applyPatch(p);
            setSelectedTableColumn(
              cols > 1 ? { elementId: element.id, col: Math.min(col, cols - 2) } : null,
            );
          }
          break;
        }
        default:
          break;
      }
    },
    [applyPatch, cols, element, setSelectedTableColumn],
  );

  const onRowMenuAction = useCallback(
    (row: number, key: Key) => {
      if (typeof key === 'string' && key.startsWith('sep-')) return;
      switch (key) {
        case 'insert-above':
          applyPatch(patchInsertRow(element, row, 'above'));
          setSelectedTableRow({ elementId: element.id, row });
          break;
        case 'insert-below':
          applyPatch(patchInsertRow(element, row, 'below'));
          setSelectedTableRow({ elementId: element.id, row: row + 1 });
          break;
        case 'duplicate':
          applyPatch(patchDuplicateRow(element, row));
          setSelectedTableRow({ elementId: element.id, row: row + 1 });
          break;
        case 'move-up':
          applyPatch(patchMoveRow(element, row, 'up'));
          if (row > 0) setSelectedTableRow({ elementId: element.id, row: row - 1 });
          break;
        case 'move-down':
          applyPatch(patchMoveRow(element, row, 'down'));
          if (row < rows - 1) setSelectedTableRow({ elementId: element.id, row: row + 1 });
          break;
        case 'delete': {
          const p = patchDeleteRow(element, row);
          if (p) {
            applyPatch(p);
            setSelectedTableRow(
              rows > 1 ? { elementId: element.id, row: Math.min(row, rows - 2) } : null,
            );
          }
          break;
        }
        default:
          break;
      }
    },
    [applyPatch, element, rows, setSelectedTableRow],
  );

  const startColBoundaryResize = (c: number, e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColIndex(c);
    if (c < cols - 1) onColResize(c, e);
    else onColEdgeResize('right', e);
  };

  const startRowBoundaryResize = (r: number, e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingRowIndex(r);
    if (r < rows - 1) onRowResize(r, e);
    else onRowEdgeResize('bottom', e);
  };

  const getTableScreenRect = useCallback(() => {
    const host = containerRef.current;
    if (!host) return null;
    return host.getBoundingClientRect();
  }, [containerRef]);

  const beginRowLineDrag = useCallback(
    (drag: NonNullable<typeof rowDragRef.current>) => {
      const tableDom = getCanvasTableDom(containerRef.current);
      const tr = tableDom?.rows[drag.fromIndex] ?? null;
      if (tr) {
        if (rowLineDragGhostTableRef.current) {
          rowLineDragGhostTableRef.current.remove();
          rowLineDragGhostTableRef.current = null;
        }
        rowLineDragGhostTableRef.current = buildRowDragGhostTable(tr);
        drag.sourceContentHidden = hideCanvasTableRowContents(tr);
      } else {
        drag.sourceContentHidden = [];
      }
      setRowMenuOpen(false);
      setColMenuOpen(false);
      suppressRowMenuClickRef.current = true;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    },
    [containerRef],
  );

  const beginColLineDrag = useCallback(
    (drag: NonNullable<typeof colDragRef.current>) => {
      const tableDom = getCanvasTableDom(containerRef.current);
      if (tableDom) {
        if (colLineDragGhostTableRef.current) {
          colLineDragGhostTableRef.current.remove();
          colLineDragGhostTableRef.current = null;
        }
        colLineDragGhostTableRef.current = buildColDragGhostTable(tableDom, drag.fromIndex);
        drag.sourceContentHidden = hideCanvasTableColumnContents(tableDom, drag.fromIndex);
      } else {
        drag.sourceContentHidden = [];
      }
      setRowMenuOpen(false);
      setColMenuOpen(false);
      suppressColMenuClickRef.current = true;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    },
    [containerRef],
  );

  const onRowGripPointerDown = (e: ReactPointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    suppressRowMenuClickRef.current = false;
    setRowMenuOpen(false);
    setColMenuOpen(false);
    selectRow(activeRow);
    const tableRect = getTableScreenRect();
    if (!tableRect) return;
    const rowBox = rowScreenBox(tableRect, rowHeights, activeRow, layoutHeight);
    rowDragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      dragging: false,
      fromIndex: activeRow,
      tableRect,
      rowBox,
    };

    const onMove = (ev: PointerEvent) => {
      const drag = rowDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      const offsetY = ev.clientY - drag.startY;
      if (!drag.dragging && Math.abs(offsetY) >= TABLE_DRAG_THRESHOLD_PX) {
        drag.dragging = true;
        beginRowLineDrag(drag);
      }
      if (!drag.dragging) return;
      const resolved = resolveCanvasRowLineDrag(
        drag.fromIndex,
        offsetY,
        drag.rowBox,
        drag.tableRect,
        rowHeights,
        layoutHeight,
      );
      setLineDragVisual({
        kind: 'row',
        tableRect: drag.tableRect,
        rowIndex: drag.fromIndex,
        rowBox: drag.rowBox,
        offsetY,
        dropLine: resolved?.dropLine ?? null,
      });
    };

    const onUp = (ev: PointerEvent) => {
      const drag = rowDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const wasDragging = drag.dragging;
      endRowLineDragVisual();
      if (wasDragging) {
        const offsetY = ev.clientY - drag.startY;
        const resolved = resolveCanvasRowLineDrag(
          drag.fromIndex,
          offsetY,
          drag.rowBox,
          drag.tableRect,
          rowHeights,
          layoutHeight,
        );
        if (resolved) {
          const to = rowMoveToIndex(drag.fromIndex, resolved.insertSlot, rows);
          const patch = patchMoveRowTo(element, drag.fromIndex, to);
          if (patch) {
            applyPatch(patch);
            setSelectedTableRow({ elementId: element.id, row: to });
          }
        }
      } else if (!suppressRowMenuClickRef.current) {
        setRowMenuOpen(true);
      }
      suppressRowMenuClickRef.current = false;
      rowDragRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const onColGripPointerDown = (e: ReactPointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    suppressColMenuClickRef.current = false;
    setRowMenuOpen(false);
    setColMenuOpen(false);
    selectColumn(activeCol);
    const tableRect = getTableScreenRect();
    if (!tableRect) return;
    const colBox = colScreenBox(tableRect, colWidths, activeCol, layoutWidth);
    colDragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      dragging: false,
      fromIndex: activeCol,
      tableRect,
      colBox,
    };

    const onMove = (ev: PointerEvent) => {
      const drag = colDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      const offsetX = ev.clientX - drag.startX;
      if (!drag.dragging && Math.abs(offsetX) >= TABLE_DRAG_THRESHOLD_PX) {
        drag.dragging = true;
        beginColLineDrag(drag);
      }
      if (!drag.dragging) return;
      const resolved = resolveCanvasColLineDrag(
        drag.fromIndex,
        offsetX,
        drag.colBox,
        drag.tableRect,
        colWidths,
        layoutWidth,
      );
      setLineDragVisual({
        kind: 'col',
        tableRect: drag.tableRect,
        colIndex: drag.fromIndex,
        colBox: drag.colBox,
        offsetX,
        dropLine: resolved?.dropLine ?? null,
      });
    };

    const onUp = (ev: PointerEvent) => {
      const drag = colDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const wasDragging = drag.dragging;
      endColLineDragVisual();
      if (wasDragging) {
        const offsetX = ev.clientX - drag.startX;
        const resolved = resolveCanvasColLineDrag(
          drag.fromIndex,
          offsetX,
          drag.colBox,
          drag.tableRect,
          colWidths,
          layoutWidth,
        );
        if (resolved) {
          const to = colMoveToIndex(drag.fromIndex, resolved.insertSlot, cols);
          const patch = patchMoveColumnTo(element, drag.fromIndex, to);
          if (patch) {
            applyPatch(patch);
            setSelectedTableColumn({ elementId: element.id, col: to });
          }
        }
      } else if (!suppressColMenuClickRef.current) {
        setColMenuOpen(true);
      }
      suppressColMenuClickRef.current = false;
      colDragRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const rowGripChromeClass =
    'flex size-full min-h-0 min-w-0 items-center justify-center rounded-sm bg-accent text-accent-foreground';
  const colGripChromeClass = rowGripChromeClass;

  const tableDomForGhost = getCanvasTableDom(containerRef.current);
  const { scaleX: ghostScaleX, scaleY: ghostScaleY } = getTableScreenScale(
    tableDomForGhost,
    layoutWidth,
    layoutHeight,
  );

  const lineDragPortal =
    portalEl &&
    lineDragVisual &&
    createPortal(
      <>
        {lineDragVisual.kind === 'row' && (() => {
          const rowIndex = lineDragVisual.rowIndex;
          const rowLogicalH =
            rowHeights[rowIndex] ?? lineDragVisual.rowBox.height / ghostScaleY;
          const gripPadL = (TABLE_ROW_GRIP_WIDTH_PX / 2) * ghostScaleX;
          const gripScreenW = TABLE_ROW_GRIP_WIDTH_PX * ghostScaleX;
          const gripScreenH = TABLE_ROW_GRIP_HEIGHT_PX * ghostScaleY;
          const ghostScaleStyle = {
            width: layoutWidth,
            height: rowLogicalH,
            transform: `scale(${ghostScaleX}, ${ghostScaleY})`,
            transformOrigin: 'top left' as const,
          };
          const contentScreen = {
            left: gripPadL,
            top: 0,
            width: lineDragVisual.tableRect.width,
            height: lineDragVisual.rowBox.height,
          };
          return (
            <div
              className="pointer-events-none fixed overflow-visible"
              style={{
                left: lineDragVisual.tableRect.left - gripPadL,
                top: lineDragVisual.rowBox.top + lineDragVisual.offsetY,
                width: lineDragVisual.tableRect.width + gripPadL,
                height: lineDragVisual.rowBox.height,
                zIndex: 230,
              }}
              aria-hidden
            >
              {/* 表格预览（底层） */}
              <div className="absolute overflow-visible" style={contentScreen}>
                <div
                  className="relative z-0 overflow-visible rounded-md bg-surface/60 shadow-lg backdrop-blur-md"
                  style={ghostScaleStyle}
                >
                  <div ref={rowLineDragGhostMountRef} className="size-full" />
                </div>
              </div>
              {/* 选框（中层，与 TableOverlays 一致，盖在预览上） */}
              <div
                className="pointer-events-none absolute z-[2] overflow-visible"
                style={contentScreen}
              >
                <div className="relative overflow-visible" style={ghostScaleStyle}>
                  <SelectionChromeFrame
                    left={0}
                    top={0}
                    width={layoutWidth}
                    height={rowLogicalH}
                    zoom={zoom}
                  />
                </div>
              </div>
              {/* 行握把（顶层，伸出表格外侧，屏幕坐标定位避免裁切） */}
              <div
                className="pointer-events-none absolute z-[3] overflow-visible rounded-sm shadow-sm"
                style={{
                  left: 0,
                  top: lineDragVisual.rowBox.height / 2 - gripScreenH / 2,
                  width: gripScreenW,
                  height: gripScreenH,
                }}
                aria-hidden
              >
                <div className={rowGripChromeClass}>
                  <IconGripVertical size={16} stroke={1.5} />
                </div>
              </div>
            </div>
          );
        })()}
        {lineDragVisual.kind === 'col' && (() => {
          const colIndex = lineDragVisual.colIndex;
          const colLogicalW =
            colWidths[colIndex] ?? lineDragVisual.colBox.width / ghostScaleX;
          const gripPadT = (TABLE_COL_GRIP_HEIGHT_PX / 2) * ghostScaleY;
          const gripScreenW = TABLE_COL_GRIP_WIDTH_PX * ghostScaleX;
          const gripScreenH = TABLE_COL_GRIP_HEIGHT_PX * ghostScaleY;
          const ghostScaleStyle = {
            width: colLogicalW,
            height: layoutHeight,
            transform: `scale(${ghostScaleX}, ${ghostScaleY})`,
            transformOrigin: 'top left' as const,
          };
          const contentScreen = {
            left: 0,
            top: gripPadT,
            width: lineDragVisual.colBox.width,
            height: lineDragVisual.tableRect.height,
          };
          return (
            <div
              className="pointer-events-none fixed overflow-visible"
              style={{
                left: lineDragVisual.colBox.left + lineDragVisual.offsetX,
                top: lineDragVisual.tableRect.top - gripPadT,
                width: lineDragVisual.colBox.width,
                height: lineDragVisual.tableRect.height + gripPadT,
                zIndex: 230,
              }}
              aria-hidden
            >
              <div className="absolute overflow-visible" style={contentScreen}>
                <div
                  className="relative z-0 overflow-visible rounded-md bg-surface/60 shadow-lg backdrop-blur-md"
                  style={ghostScaleStyle}
                >
                  <div ref={colLineDragGhostMountRef} className="size-full" />
                </div>
              </div>
              <div
                className="pointer-events-none absolute z-[2] overflow-visible"
                style={contentScreen}
              >
                <div className="relative overflow-visible" style={ghostScaleStyle}>
                  <SelectionChromeFrame
                    left={0}
                    top={0}
                    width={colLogicalW}
                    height={layoutHeight}
                    zoom={zoom}
                  />
                </div>
              </div>
              <div
                className="pointer-events-none absolute z-[3] overflow-visible rounded-sm shadow-sm"
                style={{
                  left: lineDragVisual.colBox.width / 2 - gripScreenW / 2,
                  top: 0,
                  width: gripScreenW,
                  height: gripScreenH,
                }}
                aria-hidden
              >
                <div className={colGripChromeClass}>
                  <IconGripHorizontal size={16} stroke={1.5} />
                </div>
              </div>
            </div>
          );
        })()}
        {lineDragVisual.dropLine && (
          <div
            className="pointer-events-none rounded-none bg-accent"
            style={{
              position: 'fixed',
              left: lineDragVisual.dropLine.left,
              top: lineDragVisual.dropLine.top,
              width: lineDragVisual.dropLine.width,
              height: lineDragVisual.dropLine.height,
              zIndex: 232,
              boxSizing: 'border-box',
            }}
            aria-hidden
          />
        )}
      </>,
      portalEl,
    );

  const rowGripTop =
    sumBefore(rowHeights, activeRow) + (rowHeights[activeRow] ?? 0) / 2 - TABLE_ROW_GRIP_HEIGHT_PX / 2;
  const colGripLeft =
    sumBefore(colWidths, activeCol) + (colWidths[activeCol] ?? 0) / 2 - TABLE_COL_GRIP_WIDTH_PX / 2;

  return (
    <>
      {lineDragPortal}
      <div
      data-table-axis-controls
      className="pointer-events-none absolute"
      style={{
        left: 0,
        top: 0,
        width: layoutWidth + TABLE_ADD_COL_STRIP_WIDTH_PX + TABLE_ADD_STRIP_GAP_PX,
        height: layoutHeight + TABLE_ADD_ROW_STRIP_HEIGHT_PX + TABLE_ADD_STRIP_GAP_PX,
      }}
    >
      {/* 行握把（拖拽时移至浮层，原位置隐藏） */}
      {showAxisControls && lineDragVisual?.kind !== 'row' && (
      <div
        className="pointer-events-auto absolute overflow-hidden rounded-sm shadow-sm"
        style={{
          left: -TABLE_ROW_GRIP_WIDTH_PX / 2,
          top: rowGripTop,
          width: TABLE_ROW_GRIP_WIDTH_PX,
          height: TABLE_ROW_GRIP_HEIGHT_PX,
          zIndex: 4,
        }}
      >
        <button
          type="button"
          aria-label="行：拖拽调序，单击打开菜单"
          className={`${rowGripChromeClass} cursor-grab outline-none active:cursor-grabbing`}
          onPointerDown={onRowGripPointerDown}
        >
          <IconGripVertical size={16} stroke={1.5} />
        </button>
        <Dropdown
          isOpen={rowMenuOpen}
          onOpenChange={(open) => {
            if (lineDragVisual) return;
            setRowMenuOpen(open);
          }}
          className="absolute inset-0 size-full"
        >
          <Dropdown.Trigger
            className="pointer-events-none absolute inset-0 min-h-0 min-w-0 border-0 bg-transparent p-0 opacity-0"
            aria-hidden
          >
            <span className="sr-only">行菜单</span>
          </Dropdown.Trigger>
          <Dropdown.Popover className="min-w-[200px]" placement="left" offset={6}>
          <Dropdown.Menu
            onAction={(key) => {
              onRowMenuAction(menuRow, key);
              setRowMenuOpen(false);
            }}
            disabledKeys={
              new Set([
                'sep-row-1',
                'sep-row-2',
                ...(menuRow <= 0 ? ['move-up'] : []),
                ...(menuRow >= rows - 1 ? ['move-down'] : []),
                ...(rows <= 1 ? ['delete'] : []),
              ])
            }
          >
            <Dropdown.Item id="insert-above" textValue="上方插入行">
              <IconRowInsertTop className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>上方插入行</span>
            </Dropdown.Item>
            <Dropdown.Item id="insert-below" textValue="下方插入行">
              <IconRowInsertBottom className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>下方插入行</span>
            </Dropdown.Item>
            <Dropdown.Item id="duplicate" textValue="复制行">
              <IconCopy className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>复制行</span>
            </Dropdown.Item>
            <Separator />
            <Dropdown.Item id="move-up" textValue="上移一行">
              <IconArrowAutofitUp className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>上移一行</span>
            </Dropdown.Item>
            <Dropdown.Item id="move-down" textValue="下移一行">
              <IconArrowAutofitDown className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>下移一行</span>
            </Dropdown.Item>
            <Separator />
            <Dropdown.Item id="delete" textValue="删除行" className="text-danger data-[hover=true]:text-danger">
              <IconTrash className="size-4 shrink-0 text-danger" stroke={1.5} />
              <span>删除行</span>
            </Dropdown.Item>
          </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </div>
      )}

      {/* 列握把（拖拽时移至浮层，原位置隐藏） */}
      {showAxisControls && lineDragVisual?.kind !== 'col' && (
      <div
        className="pointer-events-auto absolute overflow-hidden rounded-sm shadow-sm"
        style={{
          left: colGripLeft,
          top: -TABLE_COL_GRIP_HEIGHT_PX / 2,
          width: TABLE_COL_GRIP_WIDTH_PX,
          height: TABLE_COL_GRIP_HEIGHT_PX,
          zIndex: 4,
        }}
      >
        <button
          type="button"
          aria-label="列：拖拽调序，单击打开菜单"
          className={`${colGripChromeClass} cursor-grab outline-none active:cursor-grabbing`}
          onPointerDown={onColGripPointerDown}
        >
          <IconGripHorizontal size={16} stroke={1.5} />
        </button>
        <Dropdown
          isOpen={colMenuOpen}
          onOpenChange={(open) => {
            if (lineDragVisual) return;
            setColMenuOpen(open);
          }}
          className="absolute inset-0 size-full"
        >
          <Dropdown.Trigger
            className="pointer-events-none absolute inset-0 min-h-0 min-w-0 border-0 bg-transparent p-0 opacity-0"
            aria-hidden
          >
            <span className="sr-only">列菜单</span>
          </Dropdown.Trigger>
          <Dropdown.Popover className="min-w-[200px]" placement="top" offset={6}>
          <Dropdown.Menu
            onAction={(key) => {
              onColumnMenuAction(menuCol, key);
              setColMenuOpen(false);
            }}
            disabledKeys={
              new Set([
                'sep-col-1',
                'sep-col-2',
                ...(menuCol <= 0 ? ['move-left'] : []),
                ...(menuCol >= cols - 1 ? ['move-right'] : []),
                ...(cols <= 1 ? ['delete'] : []),
              ])
            }
          >
            <Dropdown.Item id="insert-left" textValue="左侧插入列">
              <IconColumnInsertLeft className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>左侧插入列</span>
            </Dropdown.Item>
            <Dropdown.Item id="insert-right" textValue="右侧插入列">
              <IconColumnInsertRight className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>右侧插入列</span>
            </Dropdown.Item>
            <Dropdown.Item id="duplicate" textValue="复制列">
              <IconCopy className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>复制列</span>
            </Dropdown.Item>
            <Separator />
            <Dropdown.Item id="move-left" textValue="左移一列">
              <IconArrowAutofitLeft className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>左移一列</span>
            </Dropdown.Item>
            <Dropdown.Item id="move-right" textValue="右移一列">
              <IconArrowAutofitRight className="size-4 shrink-0 opacity-80" stroke={1.5} />
              <span>右移一列</span>
            </Dropdown.Item>
            <Separator />
            <Dropdown.Item id="delete" textValue="删除列" className="text-danger data-[hover=true]:text-danger">
              <IconTrash className="size-4 shrink-0 text-danger" stroke={1.5} />
              <span>删除列</span>
            </Dropdown.Item>
          </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </div>
      )}

      {/* 列间调宽 */}
      {showAxisControls &&
        colWidths.map((_, c) => {
        const lineActive = resizingColIndex === c;
        const edgeLeft = sumBefore(colWidths, c) + colWidths[c] - COL_RESIZE_HIT_PX / 2;
        return (
          <div
            key={`col-rsz-${c}`}
            role="separator"
            aria-orientation="vertical"
            title="悬停或拖拽调整列宽"
            className="group pointer-events-auto absolute z-[3] flex cursor-col-resize justify-center"
            style={{
              left: edgeLeft,
              top: 0,
              width: COL_RESIZE_HIT_PX,
              height: layoutHeight,
            }}
            onMouseDown={(e) => startColBoundaryResize(c, e)}
            onMouseUp={() => setResizingColIndex(null)}
          >
            <span
              className={
                lineActive
                  ? 'pointer-events-none h-full w-[2px] shrink-0 bg-primary opacity-100 transition-opacity duration-150'
                  : 'pointer-events-none h-full w-[2px] shrink-0 bg-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100'
              }
              aria-hidden
            />
          </div>
        );
        })}

      {/* 行间调高 */}
      {showAxisControls &&
        rowHeights.map((_, r) => {
        const lineActive = resizingRowIndex === r;
        const edgeTop = sumBefore(rowHeights, r) + rowHeights[r] - ROW_RESIZE_HIT_PX / 2;
        return (
          <div
            key={`row-rsz-${r}`}
            role="separator"
            aria-orientation="horizontal"
            title="悬停或拖拽调整行高"
            className="group pointer-events-auto absolute z-[3] flex cursor-row-resize flex-col items-center justify-center"
            style={{
              left: 0,
              top: edgeTop,
              width: layoutWidth,
              height: ROW_RESIZE_HIT_PX,
            }}
            onMouseDown={(e) => startRowBoundaryResize(r, e)}
            onMouseUp={() => setResizingRowIndex(null)}
          >
            <span
              className={
                lineActive
                  ? 'pointer-events-none h-[2px] w-full shrink-0 bg-primary opacity-100 transition-opacity duration-150'
                  : 'pointer-events-none h-[2px] w-full shrink-0 bg-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100'
              }
              aria-hidden
            />
          </div>
        );
        })}

      {showAddStrips && (
        <button
          type="button"
          title="在表格末尾添加一行"
          aria-label="在表格末尾添加一行"
          className="pointer-events-auto absolute flex shrink-0 items-center justify-center rounded-sm border border-border bg-surface text-default-500 shadow-none transition-colors hover:bg-surface-secondary hover:text-default-foreground"
          style={{
            top: layoutHeight + TABLE_ADD_STRIP_GAP_PX,
            left: 0,
            width: layoutWidth,
            height: TABLE_ADD_ROW_STRIP_HEIGHT_PX,
            zIndex: 2,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            applyPatch(patchInsertRowAtEnd(element));
          }}
        >
          <IconPlus size={TABLE_ADD_STRIP_ICON_PX} stroke={1.5} className="shrink-0 opacity-80" />
        </button>
      )}

      {showAddStrips && (
        <button
          type="button"
          title="在表格末尾添加一列"
          aria-label="在表格末尾添加一列"
          className="pointer-events-auto absolute flex shrink-0 items-center justify-center rounded-sm border border-border bg-surface text-default-500 shadow-none transition-colors hover:bg-surface-secondary hover:text-default-foreground"
          style={{
            top: 0,
            left: layoutWidth + TABLE_ADD_STRIP_GAP_PX,
            width: TABLE_ADD_COL_STRIP_WIDTH_PX,
            height: layoutHeight,
            zIndex: 2,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            applyPatch(patchInsertColumnAtEnd(element));
          }}
        >
          <IconPlus size={TABLE_ADD_STRIP_ICON_PX} stroke={1.5} className="shrink-0 opacity-80" />
        </button>
      )}
    </div>
    </>
  );
}
